import ListView from '@/components/datagrid/ListView';
import ShipmentForm from '@/components/forms/shipment/ShipmentForm';
import useTitle from '@/hooks/useTitle';
import proxy from '@/services/ProxyService';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import useSearchStore from '@/store/search';

export default function Shipments() {
  const { config } = useRouteLoaderData('auth');
  useTitle('Lieferungen');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const { registerLocalSearch, unregisterLocalSearch } = useSearchStore();

  useEffect(() => {
    registerLocalSearch({
      label: 'Lieferungen',
      onSearch: (query) => setSearchQuery(query),
      onClear: () => setSearchQuery(''),
    });
    return () => unregisterLocalSearch();
  }, [registerLocalSearch, unregisterLocalSearch]);

  const activeRequest = useRef(0);
  const iface = config?.warehouse?.weclappInterface;

  const search = useMemo(
    () => async (str) => {
      const q = str.trim();
      if (!iface || !q) {
        setItems([]);
        return;
      }

      const reqId = ++activeRequest.current;
      setLoading(true);
      try {
        const { data: shipmentResults } = await proxy.get(
          `/${iface}/shipment?or-shipmentNumber-eq=${encodeURIComponent(
            q,
          )}&or-shipmentNumber-like=${encodeURIComponent(`LI-${q}`)}`,
        );

        if (reqId === activeRequest.current) {
          const merged = (shipmentResults?.result ?? []).map((row) => ({
            _id: row.id,
            ...row,
          }));
          setItems(merged);
        }
      } catch (_e) {
        if (activeRequest.current === reqId) setItems([]);
      } finally {
        if (activeRequest.current === reqId) setLoading(false);
      }
    },
    [iface],
  );

  useEffect(() => {
    if (!searchQuery.trim()) {
      setItems([]);
      return;
    }
    const t = setTimeout(() => search(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, search]);

  const columns = [
    {
      field: 'type',
      headerName: 'Typ',
      width: 100,
      valueGetter: (_value, row) =>
        row?.orderNumber ? 'Auftrag' : 'Lieferung',
    },
    {
      field: 'number',
      headerName: 'Nummer',
      width: 200,
      valueGetter: (_value, row) => row?.orderNumber || row?.shipmentNumber,
    },
    {
      field: 'customerName',
      headerName: 'Kunde',
      flex: 1,
      valueGetter: (_value, row) => row?.invoiceAddress?.company,
    },
    {
      field: 'commission',
      headerName: 'Kommission',
      flex: 1,
    },
  ];

  return (
    <ListView
      key={searchQuery}
      rows={items}
      columns={columns}
      actions={[]}
      loading={loading}
      detailComponents={[{ Component: ShipmentForm, title: 'Positionen' }]}
    />
  );
}
